import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiEye } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Card, DataTable, Button, Badge, ConfirmDialog, Toggle, Modal } from '../../components/common';
import { newsService } from '../../services/newsService';
import { formatDate } from '../../utils/dateUtils';
import { truncate } from '../../utils/stringUtils';
import { useDebounce } from '../../hooks';
import './NewsPage.css';

const NewsListPage = () => {
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [deleteModal, setDeleteModal] = useState({ open: false, slug: null });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [previewModal, setPreviewModal] = useState({ open: false, news: null });

  const debouncedSearch = useDebounce(search, 300);
  const pageSize = 10;

  const fetchNews = useCallback(async () => {
    setLoading(true);
    try {
      const response = await newsService.getAll({
        page: currentPage,
        limit: pageSize,
        search: debouncedSearch
      });
      setNews(response.data.news);
      setTotalItems(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch news');
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch]);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleToggleStatus = async (slug) => {
    try {
      await newsService.toggleStatus(slug);
      setNews(prev => prev.map(item => 
        item.slug === slug 
          ? { ...item, status: item.status === 'published' ? 'draft' : 'published' }
          : item
      ));
      toast.success('Status updated successfully');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await newsService.delete(deleteModal.slug);
      toast.success('News deleted successfully');
      fetchNews();
    } catch (error) {
      toast.error('Failed to delete news');
    } finally {
      setDeleteLoading(false);
      setDeleteModal({ open: false, slug: null });
    }
  };

  const columns = [
    {
      header: 'Title',
      accessor: 'title',
      render: (row) => (
        <div className="news-title-cell">
          {row.featuredImage && (
            <img src={row.featuredImage} alt="" className="news-thumbnail" />
          )}
          <div className="news-title-content">
            <span className="news-title">{truncate(row.title, 50)}</span>
            <span className="news-date">{formatDate(row.createdAt)}</span>
          </div>
        </div>
      )
    },
    {
      header: 'Status',
      accessor: 'status',
      width: '120px',
      render: (row) => (
        <Toggle
          name={`status-${row.slug}`}
          checked={row.status === 'published'}
          onChange={() => handleToggleStatus(row.slug)}
          size="small"
        />
      )
    },
    {
      header: 'State',
      accessor: 'statusLabel',
      width: '100px',
      render: (row) => (
        <Badge variant={row.status === 'published' ? 'success' : 'warning'}>
          {row.status === 'published' ? 'Published' : 'Draft'}
        </Badge>
      )
    },
    {
      header: 'Author',
      accessor: 'author',
      width: '100px'
    },
    {
      header: 'Actions',
      width: '120px',
      align: 'center',
      render: (row) => (
        <div className="action-buttons">
          <button 
            className="action-btn view"
            title="View"
            onClick={() => setPreviewModal({ open: true, news: row })}
          >
            <FiEye />
          </button>
          <button 
            className="action-btn edit"
            title="Edit"
            onClick={() => navigate(`/news/edit/${row.slug}`)}
          >
            <FiEdit2 />
          </button>
          <button 
            className="action-btn delete"
            title="Delete"
            onClick={() => setDeleteModal({ open: true, slug: row.slug })}
          >
            <FiTrash2 />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="news-page">
      <Card
        title="All News"
        subtitle={`${totalItems} news articles`}
        action={
          <Link to="/news/add">
            <Button icon={<FiPlus />}>Add News</Button>
          </Link>
        }
      >
        <DataTable
          columns={columns}
          data={news}
          loading={loading}
          searchable
          searchPlaceholder="Search news..."
          searchValue={search}
          onSearch={handleSearch}
          pagination
          pageSize={pageSize}
          totalItems={totalItems}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          exportable
          exportFilename="news-export"
          emptyMessage="No news articles found"
        />
      </Card>

      <ConfirmDialog
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, slug: null })}
        onConfirm={handleDelete}
        title="Delete News"
        message="Are you sure you want to delete this news article? This action cannot be undone."
        confirmText="Delete"
        loading={deleteLoading}
      />

      <Modal
        isOpen={previewModal.open}
        onClose={() => setPreviewModal({ open: false, news: null })}
        title={previewModal.news?.title || ''}
        size="large"
      >
        {previewModal.news && (
          <div className="news-preview">
            {previewModal.news.featuredImage && (
              <img
                src={previewModal.news.featuredImage}
                alt={previewModal.news.title}
                className="news-preview-image"
              />
            )}
            <div className="news-preview-meta">
              <Badge variant={previewModal.news.status === 'published' ? 'success' : 'warning'}>
                {previewModal.news.status === 'published' ? 'Published' : 'Draft'}
              </Badge>
              <span className="news-preview-date">{formatDate(previewModal.news.createdAt)}</span>
              {previewModal.news.author && (
                <span className="news-preview-author">By {previewModal.news.author}</span>
              )}
            </div>
            {previewModal.news.excerpt && (
              <p className="news-preview-excerpt">{previewModal.news.excerpt}</p>
            )}
            <div
              className="news-preview-content"
              dangerouslySetInnerHTML={{ __html: previewModal.news.content }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default NewsListPage;
